import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { MeteorObservable } from 'meteor-rxjs';
import { Subscription } from 'rxjs';

import { MatDialog } from '@angular/material/dialog';
import {
  addDays,
  addMonths,
  differenceInCalendarMonths,
  endOfISOWeek,
  endOfMonth,
  format,
  fromUnixTime,
  getUnixTime,
  isAfter,
  isBefore,
  startOfDay,
  startOfMonth,
  subDays,
} from 'date-fns';
import { utcDifferenceInDays } from 'server/helpers/utcDifferenceInDays';
import { Users } from 'server/shared/collections/users';
import {
  createEmploymentAvailability,
  deleteEmploymentAvailability,
  updateEmploymentAvailability,
} from 'server/shared/helpers/employmentAvailabilityHelper';
import { randomId } from 'server/shared/helpers/random';
import { Customer } from 'server/shared/models/customer';
import { RoleEnum } from 'server/shared/models/role.enum';
import {
  EmploymentAvailability,
  EmploymentAvailabilityStatusEnum,
  PoolerUser,
  UnavailabilityReasonEnum,
} from 'server/shared/models/user';
import { dbDayDateToken } from 'server/static/dbDayDateToken';
import { CustomerService } from '../../services/customer.service';
import { EditDateRangeDialog } from '../../shared/date-range-dialog/date-range-dialog.component';
import {
  EmploymentAvailabilityEditDialogComponent,
  EmploymentAvailabilityEditDialogData,
} from './availability-edit-dialog/availability-edit-dialog.component';
import { EmploymentAvailabilityMonth } from './employment-availability-month.model';
import {
  EmploymentAvailabilityWorkingJobDialogComponent,
  EmploymentAvailabilityWorkingJobDialogData,
} from './employment-availability-working-job-dialog/employment-availability-working-job-dialog';

let times = require('lodash/times');

export interface ExpandedEmploymentAvailabilities
  extends EmploymentAvailability {
  daysBetween: number;
  indentation: number;
  calendarStart: string;
  calendarEnd: string;
  isOwnBooked?: boolean;
}

@Component({
  selector: 'availability-edit',
  templateUrl: 'availability-edit.html',
  styleUrls: ['availability-edit.scss'],
})
export class EmploymentAvailabilityEditComponent implements OnInit, OnDestroy {
  user?: PoolerUser;
  userSubscription?: Subscription;

  statusEnum = EmploymentAvailabilityStatusEnum;

  expandedEmploymentAvailabilities: ExpandedEmploymentAvailabilities[] = [];

  isDispoEditingPooler: boolean = false;
  meteorLiveQueryHandlers: Meteor.LiveQueryHandle[] = [];

  customDateRange: boolean = false;
  rangeStartDate?: Date = new Date();
  rangeEndDate?: Date;

  today: string = format(new Date(), dbDayDateToken);

  dispoCustomer?: Customer;

  private _monthsInFuture: number = 11;
  public months: Array<EmploymentAvailabilityMonth> = [];

  constructor(
    private ngZone: NgZone,
    private location: Location,
    private route: ActivatedRoute,
    private customerService: CustomerService,
    private dialog: MatDialog,
  ) {}

  async ngOnInit(): Promise<void> {
    // queryParamsUserId is only for dispos
    if (this.queryParamsUserId) {
      this.dispoCustomer = await this.customerService.readyDispoCustomer;
      this.isDispoEditingPooler = true;
      this.userSubscription = MeteorObservable.subscribe(
        'myProfile',
        this.queryParamsUserId,
      ).subscribe(() => {
        this._setUser(this.queryParamsUserId);
        this.prepareMonths();
      });
      const liveQueryHandler = Users.find(this.queryParamsUserId).observe({
        changed: () => {
          this._setUser(this.queryParamsUserId);
        },
      });
      this.meteorLiveQueryHandlers.push(liveQueryHandler);
    } else {
      const tracker = Tracker.autorun(() => {
        this.ngZone.run(() => {
          const userId = Meteor.userId();
          if (!userId) return;
          this._setUser(userId);
          this.prepareMonths();
        });
      });
      this.meteorLiveQueryHandlers.push(tracker);
    }
  }

  private _setUser(userId: string) {
    this.ngZone.run(() => {
      let user = Users.findOne({
        _id: userId,
        role: RoleEnum.POOL,
      }) as PoolerUser;
      if (user) {
        this.expandedEmploymentAvailabilities = [];

        const calendarStartUnix = getUnixTime(
          this.rangeStartDate || new Date(),
        );
        const calendarEndUnix = getUnixTime(
          this.rangeEndDate ||
            endOfMonth(addMonths(new Date(), this._monthsInFuture)),
        );
        user.employmentAvailabilities?.forEach((availability) => {
          let startDateUnix = getUnixTime(new Date(availability.startDate));
          let endDateUnix = getUnixTime(new Date(availability.endDate));

          if (
            startDateUnix < calendarStartUnix &&
            endDateUnix > calendarStartUnix
          ) {
            startDateUnix = calendarStartUnix;
          }

          if (
            endDateUnix > calendarEndUnix &&
            startDateUnix < calendarEndUnix
          ) {
            endDateUnix = calendarEndUnix;
          }

          const daysBetween = utcDifferenceInDays(
            fromUnixTime(endDateUnix),
            fromUnixTime(startDateUnix),
          );

          const overlappingAvailabilities =
            user.employmentAvailabilities?.filter((otherAvailability) => {
              const otherStartDateUnix = getUnixTime(
                new Date(otherAvailability.startDate),
              );
              const otherEndDateUnix = getUnixTime(
                new Date(otherAvailability.endDate),
              );
              return (
                (otherStartDateUnix >= startDateUnix &&
                  otherStartDateUnix <= endDateUnix) ||
                (otherEndDateUnix <= endDateUnix &&
                  otherEndDateUnix >= startDateUnix) ||
                (startDateUnix >= otherStartDateUnix &&
                  startDateUnix <= otherEndDateUnix) ||
                (endDateUnix >= otherStartDateUnix &&
                  endDateUnix <= otherEndDateUnix)
              );
            });

          let indentation = 0;
          overlappingAvailabilities.forEach((otherAvailability) => {
            const expandedAvailability =
              this.expandedEmploymentAvailabilities.find(
                (expandedAvailability) =>
                  expandedAvailability.id === otherAvailability.id,
              );
            if (
              availability.status !== EmploymentAvailabilityStatusEnum.AVAILABLE
            ) {
              if (
                otherAvailability.status ===
                  EmploymentAvailabilityStatusEnum.AVAILABLE &&
                indentation === 0
              ) {
                indentation = 1;
              } else {
                if (
                  availability.status ===
                  EmploymentAvailabilityStatusEnum.WORKING
                ) {
                  if (
                    otherAvailability.status ===
                    EmploymentAvailabilityStatusEnum.UNAVAILABLE
                  ) {
                    if (expandedAvailability) {
                      indentation = expandedAvailability.indentation + 1;
                    } else {
                      indentation++;
                    }
                  } else if (
                    otherAvailability.status ===
                    EmploymentAvailabilityStatusEnum.WORKING
                  ) {
                    if (availability.startDate > otherAvailability.startDate) {
                      if (expandedAvailability) {
                        indentation = expandedAvailability.indentation + 1;
                      } else {
                        indentation++;
                      }
                    }
                  }
                } else if (
                  availability.status ===
                    EmploymentAvailabilityStatusEnum.UNAVAILABLE &&
                  otherAvailability.status ===
                    EmploymentAvailabilityStatusEnum.UNAVAILABLE &&
                  availability.startDate > otherAvailability.startDate
                ) {
                  if (expandedAvailability) {
                    indentation = expandedAvailability.indentation + 1;
                  } else {
                    indentation++;
                  }
                }
              }
            }
          });
          const isOwnBooked =
            !this.dispoCustomer ||
            (availability.bookedEmployment &&
              availability.bookedEmployment?.inviteeCustomerId ===
                this.dispoCustomer?._id);
          this.expandedEmploymentAvailabilities.push({
            ...availability,
            isOwnBooked,
            daysBetween: daysBetween + 1,
            indentation: indentation,
            calendarStart: format(fromUnixTime(startDateUnix), dbDayDateToken),
            calendarEnd: format(fromUnixTime(endDateUnix), dbDayDateToken),
          });
        });
        this.user = user;
      }
    });
  }

  private get queryParamsUserId(): string {
    return this.route.snapshot.queryParams['userId'];
  }

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    if (
      this.meteorLiveQueryHandlers &&
      this.meteorLiveQueryHandlers.length > 0
    ) {
      this.meteorLiveQueryHandlers.forEach((liveQueryHandler) => {
        liveQueryHandler.stop();
      });
    }
  }

  editAvailability(availabilityId?: string): void {
    let availability = this.user?.employmentAvailabilities?.find(
      (availability) => availability.id === availabilityId,
    );
    if (!availability) return;

    if (
      availability.status === EmploymentAvailabilityStatusEnum.WORKING &&
      availability.bookedEmployment
    ) {
      const availabilityData: EmploymentAvailabilityWorkingJobDialogData = {
        availabilityId: availability.id,
        startDate: availability.startDate,
        endDate: availability.endDate,
        personName: this.user?.firstName + ' ' + this.user?.lastName,
        organisationName: availability.bookedEmployment.organisation.name,
        departmentName: availability.bookedEmployment.department.name,
        unavailabilities: availability.bookedEmployment.unavailabilities,
        isOwnBooked:
          !this.dispoCustomer ||
          availability.bookedEmployment.inviteeCustomerId ===
            this.dispoCustomer?._id,
      };
      this.openWorkingEmploymentDialog(availabilityData);
    } else {
      const availabilityData: EmploymentAvailabilityEditDialogData = {
        personName: this.user?.firstName + ' ' + this.user?.lastName,
        availabilityId: availabilityId,
        startDate: availability.startDate,
        endDate: availability.endDate,
        notes: availability.notes,
        status: availability.status,
        unavailableReason: availability.unavailableReason,
        workload: availability.workload,
      };
      this.openDialog(availabilityData);
    }
  }

  openDialog(availabilityData?: EmploymentAvailabilityEditDialogData): void {
    if (!this.user) return;
    const dialogRef = this.dialog.open(
      EmploymentAvailabilityEditDialogComponent,
      {
        data: availabilityData
          ? availabilityData
          : {
              personName: this.user.firstName + ' ' + this.user.lastName,
            },
      },
    );

    dialogRef
      .afterClosed()
      .subscribe(
        async (result: {
          id?: string;
          startDate: string;
          endDate: string;
          status: EmploymentAvailabilityStatusEnum;
          workload?: number;
          unavailableReason?: UnavailabilityReasonEnum;
          notes?: string;
          toDelete?: boolean;
        }) => {
          if (result && this.user) {
            if (result.id) {
              if (result.toDelete) {
                const { employmentAvailabilities, employmentAvailabilityDays } =
                  deleteEmploymentAvailability(
                    result.id,
                    this.user.employmentAvailabilities || [],
                    this.user.employmentAvailabilityDays || [],
                  );
                this.user.employmentAvailabilities = employmentAvailabilities;
                this.user.employmentAvailabilityDays =
                  employmentAvailabilityDays;
              } else {
                const { employmentAvailabilities, employmentAvailabilityDays } =
                  updateEmploymentAvailability(
                    result.id,
                    result as EmploymentAvailability,
                    this.user.employmentAvailabilities || [],
                    this.user.employmentAvailabilityDays || [],
                  );
                this.user.employmentAvailabilities = employmentAvailabilities;
                this.user.employmentAvailabilityDays =
                  employmentAvailabilityDays;
              }
            } else {
              const availability = {
                id: randomId(),
                startDate: result.startDate,
                endDate: result.endDate,
                status: result.status,
                unavailableReason: result.unavailableReason,
                notes: result.notes,
              };
              const { employmentAvailabilities, employmentAvailabilityDays } =
                createEmploymentAvailability(
                  availability,
                  this.user.employmentAvailabilities || [],
                  this.user.employmentAvailabilityDays || [],
                );
              this.user.employmentAvailabilities = employmentAvailabilities;
              this.user.employmentAvailabilityDays = employmentAvailabilityDays;
            }
            Meteor.call(
              'updateEmploymentAvailabilitiesByClient',
              this.user._id,
              this.user.employmentAvailabilities,
              this.user.employmentAvailabilityDays,
            );

            this.prepareMonths();
          }
        },
      );
  }

  openWorkingEmploymentDialog(
    availabilityData: EmploymentAvailabilityWorkingJobDialogData,
  ) {
    const dialogRef = this.dialog.open(
      EmploymentAvailabilityWorkingJobDialogComponent,
      {
        data: availabilityData,
      },
    );
  }

  openDateRangeDialog(): void {
    const dialogRef = this.dialog.open(EditDateRangeDialog, {
      data: {
        fromDate: this.rangeStartDate ? this.rangeStartDate : new Date(),
        toDate: this.rangeEndDate
          ? this.rangeEndDate
          : endOfISOWeek(new Date()),
      },
    });

    dialogRef
      .afterClosed()
      .subscribe((result: { fromDate: Date; toDate: Date }) => {
        if (result) {
          this.rangeStartDate = result.fromDate;
          this.rangeEndDate = result.toDate;
          this.prepareMonths();
        }
      });
  }

  historyBack() {
    this.location.back();
  }

  prepareMonths(): void {
    this.months = [];
    if (this.customDateRange && this.rangeStartDate && this.rangeEndDate) {
      this.months.push(
        new EmploymentAvailabilityMonth(startOfDay(this.rangeStartDate)),
      );
      const monthsBetween = differenceInCalendarMonths(
        startOfMonth(this.rangeEndDate),
        startOfMonth(this.rangeStartDate),
      );
      times(monthsBetween, (index: number) => {
        this.months.push(
          new EmploymentAvailabilityMonth(
            addMonths(startOfMonth(new Date()), index + 1),
            index === monthsBetween - 1 ? this.rangeEndDate : undefined,
          ),
        );
      });
    } else {
      if (this.queryParamsUserId) {
        if (!this.dispoCustomer) return;
        let joinDate: Date | undefined = this.user?.customerInformation?.[
          this.dispoCustomer._id
        ]?.joinDate
          ? new Date(
              this.user.customerInformation[this.dispoCustomer._id].joinDate!,
            )
          : undefined;
        let leaveDate: Date | undefined = this.user?.customerInformation?.[
          this.dispoCustomer._id
        ]?.leaveDate
          ? new Date(
              this.user.customerInformation[this.dispoCustomer._id].leaveDate!,
            )
          : undefined;
        if (leaveDate && joinDate && joinDate > leaveDate) {
          this.months.push(
            new EmploymentAvailabilityMonth(
              startOfDay(new Date()),
              undefined,
              addDays(leaveDate, 1),
              subDays(joinDate, 1),
            ),
          );
          times(this._monthsInFuture, (index: number) => {
            if (joinDate && leaveDate) {
              this.months.push(
                new EmploymentAvailabilityMonth(
                  addMonths(startOfMonth(new Date()), index + 1),
                  undefined,
                  addDays(leaveDate, 1),
                  subDays(joinDate, 1),
                ),
              );
            }
          });
        } else {
          if (!joinDate || isBefore(joinDate, new Date())) {
            joinDate = new Date();
          }
          this.months.push(
            new EmploymentAvailabilityMonth(startOfDay(joinDate), leaveDate),
          );
          if (isAfter(startOfMonth(joinDate), startOfMonth(new Date()))) {
            this._monthsInFuture =
              this._monthsInFuture -
              differenceInCalendarMonths(
                startOfMonth(joinDate),
                startOfMonth(new Date()),
              );
          }
          times(this._monthsInFuture, (index: number) => {
            this.months.push(
              new EmploymentAvailabilityMonth(
                addMonths(startOfMonth(joinDate || new Date()), index + 1),
                leaveDate,
              ),
            );
          });
        }
      } else {
        this.months.push(
          new EmploymentAvailabilityMonth(startOfDay(new Date())),
        );
        times(this._monthsInFuture, (index: number) => {
          this.months.push(
            new EmploymentAvailabilityMonth(
              addMonths(startOfMonth(new Date()), index + 1),
            ),
          );
        });
      }
    }
  }
}
